import { FilterToolbar, PageCanvas } from "@/components/page-canvas";
import { Head, Link, usePage } from "@inertiajs/react";
import { useTranslation } from "react-i18next";
import AppLayout from "@/layouts/app_layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { locationRequestsPath, newLocationRequestPath } from "@/routes";
import { Choice } from "./controls";
import { RequestLedger } from "./request-cell";
import type { Enquiry, Pagination } from "./types";

export default function RequestsIndex({
  enquiries,
  pagination,
  filters,
}: {
  enquiries: Enquiry[];
  pagination: Pagination;
  filters: { q?: string; state?: string };
}) {
  const { auth } = usePage().props;
  const { t } = useTranslation();
  if (!auth.location) return null;
  const locationId = auth.location.id;
  const path = locationRequestsPath(locationId);
  return (
    <AppLayout breadcrumbs={[{ title: t("requests.title"), href: path }]}>
      <Head title={t("requests.title")} />
      <PageCanvas>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-page font-display">{t("requests.title")}</h1>
            <p className="text-muted-foreground">{auth.location.name}</p>
          </div>
          <Button render={<Link href={newLocationRequestPath(locationId)} />}>
            {t("requests.add")}
          </Button>
        </div>
        <FilterToolbar action={path} method="get">
          <div className="space-y-2">
            <Label htmlFor="request-search">{t("requests.search")}</Label>
            <Input
              id="request-search"
              name="q"
              defaultValue={filters.q}
              placeholder={t("product_ux_staff.identity_search")}
              aria-describedby="request-search-help"
            />
          </div>
          <Choice
            id="request-state-filter"
            name="state"
            label={t("requests.state")}
            defaultValue={filters.state || "all"}
            options={[
              { id: "all", name: t("requests.all") },
              ...[
                "new",
                "responded",
                "accepted",
                "declined",
                "rescheduled",
              ].map((id) => ({ id, name: t(`requests.states.${id}`) })),
            ]}
          />
          <Button
            variant="outline"
            type="submit"
            className="justify-self-start"
          >
            {t("requests.filter")}
          </Button>
        </FilterToolbar>
        <p id="request-search-help" className="text-muted-foreground text-sm">
          {t("product_ux_staff.identity_search_help")}
        </p>
        {enquiries.length === 0 && (
          <div className="space-y-2 rounded-lg border p-4">
            <p className="text-muted-foreground">
              {t(
                filters.q || (filters.state && filters.state !== "all")
                  ? "product_ux_staff.no_matches"
                  : "requests.empty",
              )}
            </p>
            {(filters.q || (filters.state && filters.state !== "all")) && (
              <Link
                href={path}
                className="inline-flex min-h-11 items-center underline"
              >
                {t("product_ux_staff.clear_filters")}
              </Link>
            )}
          </div>
        )}
        {enquiries.length > 0 && (
          <RequestLedger enquiries={enquiries} locationId={locationId} />
        )}
        <nav
          aria-label={t("requests.pages")}
          className="flex flex-wrap items-center gap-4"
        >
          {pagination.page > 1 && (
            <Link
              className="underline"
              href={locationRequestsPath(locationId, {
                ...filters,
                page: pagination.page - 1,
              })}
            >
              {t("requests.previous")}
            </Link>
          )}
          <span>{t("requests.page", pagination)}</span>
          {pagination.page < pagination.pages && (
            <Link
              className="underline"
              href={locationRequestsPath(locationId, {
                ...filters,
                page: pagination.page + 1,
              })}
            >
              {t("requests.next")}
            </Link>
          )}
        </nav>
      </PageCanvas>
    </AppLayout>
  );
}
